import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SeoHead } from '../components/SeoHead';
import { getStaticPageMetadata, BLOG_ROBOTS } from '../lib/seo';
import { PageContainer } from '../src/components/ui/page-container';
import { PageTitle } from '../src/components/ui/typography';
import { EmptyState } from '../src/components/ui/empty-state';
import { Button } from '../src/components/ui/button';
import { getPublishedPostsByCategory } from '../services/blogService';
import { toast } from '../src/lib/toast';
import type { HydratedBlogPost } from '../src/types/blog';
import { getSSGPageData } from '../lib/ssGData';
import { toSeededPosts, readTimeBySlug } from '../lib/blogSeed';
import PostCard from '../src/components/blog/PostCard';
import PostCardSkeleton from '../src/components/blog/PostCardSkeleton';
import CategoryPillNav from '../src/components/blog/CategoryPillNav';
import BlogCtaCard from '../src/components/blog/BlogCtaCard';
import BeltIcon from '../src/components/ui/BeltIcon';
import { CATEGORY_LABELS, isBlogCategory, POSTS_PER_PAGE, postsPerPageFor } from '../src/constants/blog';
import { useBreakpointUp } from '../hooks/useBreakpointUp';

const BlogCategoryPage: React.FC = () => {
    const { category: categoryParam } = useParams<{ category: string }>();
    const category = isBlogCategory(categoryParam) ? categoryParam : null;

    // H7: first paint comes from the prerendered post list when there is one, so the
    // page a crawler reads is the page a visitor sees. isLoading starts FALSE in that
    // case — starting true would paint skeletons over content that is already in the
    // html, which is both a visible flash and a hydration mismatch.
    // Adopt the build's list ONLY when it was built for THIS category — see the same
    // guard in BlogIndexPage. Keyed on `category` so a client-side pill switch (which does
    // NOT remount this component: App.tsx declares /blog/:category without a key) stops
    // trusting the previous category's payload and falls back to fetching.
    const ssgBlogPosts = useMemo(() => {
        const d = getSSGPageData();
        return d?.blogPosts && d.blogListingCategory === category ? d.blogPosts : undefined;
    }, [category]);
    const seededPosts = useMemo(() => toSeededPosts(ssgBlogPosts), [ssgBlogPosts]);
    const seededReadTimes = useMemo(() => readTimeBySlug(ssgBlogPosts), [ssgBlogPosts]);
    const [posts, setPosts] = useState<HydratedBlogPost[]>(seededPosts);
    const [isLoading, setIsLoading] = useState(seededPosts.length === 0);
    const [isError, setIsError] = useState(false);
    const [visibleCount, setVisibleCount] = useState<number>(POSTS_PER_PAGE);
    // Responsive page size: 9 on desktop (lg+), 6 below (mobile/tablet).
    // Item 1: this follows the stylesheet's own `lg:` tier, asked of the browser in
    // REM, rather than comparing a pixel width to 1024 — the two disagree for a
    // visitor with a raised default font size. `useBreakpointUp` returns the
    // SSR-safe answer on the first render, so the initial slice still matches the
    // SSG HTML exactly, which is the property the floor below depends on.
    const isLgUp = useBreakpointUp('lg');
    const pageSize = postsPerPageFor(isLgUp);

    // Re-invocable fetch — called on mount/category-change and by the
    // error-state "Try again" retry. loadPosts closes over the current
    // category (dep below), so a category change re-creates it and the effect
    // re-runs. The request-id guard ignores stale resolutions (unmount or a
    // rapid category switch), replacing the previous per-effect `cancelled`
    // flag. On failure: distinct error state + toast (item 25; toast item 22).
    const requestIdRef = useRef(0);
    const loadPosts = useCallback(() => {
        if (!category) {
            setIsLoading(false);
            return;
        }
        const requestId = ++requestIdRef.current;
        setIsLoading(true);
        setIsError(false);
        getPublishedPostsByCategory(category)
            .then((result) => {
                if (requestId !== requestIdRef.current) return;
                setPosts(result);
                setIsLoading(false);
            })
            .catch(() => {
                if (requestId !== requestIdRef.current) return;
                setIsError(true);
                setIsLoading(false);
                toast.error("Couldn't load posts. Please try again.");
            });
    }, [category]);

    useEffect(() => {
        // H7: when the build shipped a post list, THAT is the truth and we do not fetch.
        // A post exists in Firestore before the platform publishes it — publication is the
        // DEPLOY, on a deliberate delay — so fetching here surfaced posts whose pages are
        // not built yet: a visitor could click a card and land on the not-found shell, and
        // the list visibly reshuffled a moment after load. Trusting the deployed list fixes
        // both. The fetch remains the fallback for local dev, where there is no build data.
        if (seededPosts.length > 0) return;
        loadPosts();
    }, [loadPosts, seededPosts.length]);

    // Reset the visible slice whenever either the posts identity OR the active
    // category route param changes — navigating between /blog/beginners and
    // /blog/style-guides must drop back to the first page — but NEVER below what the
    // server already rendered.
    //
    // H7 made that floor necessary: the server renders POSTS_PER_PAGE because the
    // breakpoint reports its SSR-safe answer (lg+) on the first render, and on a phone
    // the real answer arrives moments later and drops pageSize to 6 — which without the
    // floor would delete cards the visitor is already looking at.
    const ssrRenderedCount = seededPosts.length > 0 ? Math.min(POSTS_PER_PAGE, seededPosts.length) : 0;
    useEffect(() => {
        setVisibleCount(Math.max(pageSize, ssrRenderedCount));
    }, [posts, category, pageSize, ssrRenderedCount]);

    const paginatedPosts = useMemo(
        () => posts.slice(0, visibleCount),
        [posts, visibleCount]
    );

    const hasMore = posts.length > paginatedPosts.length;

    const handleLoadMore = () => setVisibleCount((prev) => prev + pageSize);

    const meta = useMemo(
        () => (category ? { ...getStaticPageMetadata(`/blog/${category}`)!, robots: BLOG_ROBOTS } : null),
        [category]
    );

    return (
        <div className="min-h-screen bg-surface-canvas">
            {meta && <SeoHead meta={meta} />}
            <div className="h-16" />
            <main id="main-content" className="text-text-primary">
                <PageContainer size="content" className="py-12">
                    {category ? (
                        <>
                            {/* Header: H1 + descriptor only. Breadcrumb removed — CategoryPillNav below
                                provides sibling navigation; the header logo covers the "back to home" path. */}
                            <header className="mb-4">
                                {/* min-h-[35px] matches /blog's reserved heading height so switching
                                    categories doesn't shift the page (see BlogIndexPage). */}
                                <div className="flex items-center gap-4 min-h-[35px]">
                                    {/* MAN belt motif — steel blue, h-6 (≈57px wide), paired with the category title to mirror /blog. */}
                                    <BeltIcon variant="blue" className="h-6 w-auto flex-shrink-0" />
                                    <PageTitle className="text-brand-primary">{CATEGORY_LABELS[category]}</PageTitle>
                                </div>
                            </header>

                            {/* Shared category nav — current category highlighted. The "All" pill returns to /blog. */}
                            <CategoryPillNav activeCategory={category} />

                            {isLoading ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" role="status" aria-label="Loading articles" aria-busy="true">
                                    {Array.from({ length: pageSize }).map((_, i) => (
                                        <PostCardSkeleton key={i} />
                                    ))}
                                </div>
                            ) : isError ? (
                                <EmptyState
                                    headingLevel={2}
                                    title="Couldn't load posts"
                                    description="Something went wrong. Please try again."
                                    action={
                                        <Button variant="secondary" onClick={loadPosts}>
                                            Try again
                                        </Button>
                                    }
                                />
                            ) : posts.length === 0 ? (
                                <EmptyState
                                    headingLevel={2}
                                    title="No posts in this category yet"
                                    description="Check back soon."
                                />
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {paginatedPosts.map((post) => (
                                        <PostCard key={post.id} post={post} readTime={seededReadTimes[post.slug]} />
                                    ))}
                                </div>
                            )}

                            {/* Client-side Load More — mirrors DirectoryPage's idiom
                                (pages/DirectoryPage.tsx:564-570) with col-span-full
                                dropped because the button sits OUTSIDE the grid. */}
                            {hasMore && (
                                <div className="flex justify-center py-4">
                                    <Button variant="primary" onClick={handleLoadMore} className="px-6">
                                        Load More ({posts.length - paginatedPosts.length} remaining)
                                    </Button>
                                </div>
                            )}

                            <BlogCtaCard className="mt-12" />
                        </>
                    ) : (
                        <EmptyState
                            headingLevel={1}
                            title="Category not found"
                            description="That blog category doesn't exist."
                        />
                    )}
                </PageContainer>
            </main>
        </div>
    );
};

export default BlogCategoryPage;
